"use client";
import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { TextField, Button, IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import MainContainer from "../../layout/MainContainer";
import { usePostChangePasswordMutation } from "../../services/auth";
import { showError, showToast } from "../../constants/toasts";

const ChangePassword = () => {
  const navigate = useNavigate();
  const [changePassword, { isLoading }] = usePostChangePasswordMutation();

  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const formik = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required("Current password is required"),
      newPassword: Yup.string()
        .required("New password is required")
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\W).{8,}$/,
          "Password must be at least 8 characters long and include 1 uppercase, 1 lowercase, and 1 special character."
        )
        .notOneOf(
          [Yup.ref("oldPassword"), null],
          "New password can't be the same as old password"
        ),
      confirmPassword: Yup.string()
        .required("Confirm password is required")
        .oneOf([Yup.ref("newPassword")], "Passwords should match"),
    }),
    onSubmit: async (values) => {
      const body = {
        oldPassword: values.oldPassword,
        password: values.newPassword,
        confirmPassword: values.confirmPassword,
      };

      try {
        const response = await changePassword(body).unwrap();
        if (response?.statusCode === 200) {
          showToast("Password changed successfully");
          navigate(-1);
        }
      } catch (error: any) {
        showError(error?.data?.message || "Something went wrong");
      }
    },
  });

  return (
    <MainContainer>
      <div className="main_title">
        <h1>Change Password</h1>
        <p>
          <span onClick={() => navigate(-1)}>Dashboard</span> - Change Password
        </p>
      </div>

      <div className="cards">
        <form onSubmit={formik.handleSubmit} className="form">
          <div className="gap_p">
            <div className="control_group w_33">
              <label>Old Password</label>
              <TextField
                name="oldPassword"
                placeholder="Enter Old Password"
                fullWidth
                hiddenLabel
                type={showOld ? "text" : "password"}
                value={formik.values.oldPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.oldPassword &&
                  Boolean(formik.errors.oldPassword)
                }
                helperText={
                  formik.touched.oldPassword && formik.errors.oldPassword
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="eye_btn">
                      <IconButton
                        onClick={() => setShowOld((prev) => !prev)}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {showOld ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div className="control_group w_33">
              <label>New Password</label>
              <TextField
                name="newPassword"
                placeholder="Enter New Password"
                fullWidth
                hiddenLabel
                type={showNew ? "text" : "password"}
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.newPassword &&
                  Boolean(formik.errors.newPassword)
                }
                helperText={
                  formik.touched.newPassword && formik.errors.newPassword
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="eye_btn">
                      <IconButton
                        onClick={() => setShowNew((prev) => !prev)}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {showNew ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div className="control_group w_33">
              <label>Confirm New Password</label>
              <TextField
                name="confirmPassword"
                placeholder="Enter Confirm Password"
                fullWidth
                hiddenLabel
                type={showConfirm ? "text" : "password"}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.confirmPassword &&
                  Boolean(formik.errors.confirmPassword)
                }
                helperText={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="eye_btn">
                      <IconButton
                        onClick={() => setShowConfirm((prev) => !prev)}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </div>

          <div className="form_btn">
            <Button
              className="btn btn_primary"
              type="submit"
              disabled={isLoading || formik.isSubmitting}
            >
              {isLoading ? "Updating..." : "Submit"}
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default ChangePassword;
